import React, { Fragment } from 'react';
import {
  Dialog as HDialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Description,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from './Button';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const sizeMap: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
};

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: DialogSize;
  hideCloseButton?: boolean;
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
}

/**
 * Accessible dialog. Headless UI handles focus-trap, ESC, aria-modal, scroll-lock.
 * Use ConfirmDialog for simple confirm flows.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideCloseButton,
  initialFocus,
}: DialogProps) {
  return (
    <Transition show={open} as={Fragment}>
      <HDialog as="div" className="relative z-50" onClose={onClose} initialFocus={initialFocus}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-neutral-900/50 dark:bg-neutral-950/70 backdrop-blur-[1px]" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 translate-y-3 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-3 sm:scale-95"
            >
              <DialogPanel
                className={cn(
                  'relative w-full overflow-hidden rounded-xl bg-white dark:bg-neutral-900 shadow-modal',
                  'dark:bg-neutral-900 dark:ring-1 dark:ring-neutral-800',
                  sizeMap[size]
                )}
              >
                {!hideCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close dialog"
                    className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 hover:dark:text-neutral-400 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
                {(title || description) && (
                  <div className="px-6 pt-6">
                    {title && (
                      <DialogTitle className="text-h3 text-neutral-900 dark:text-neutral-100">
                        {title}
                      </DialogTitle>
                    )}
                    {description && (
                      <Description className="mt-1 text-body-sm text-neutral-600 dark:text-neutral-400">
                        {description}
                      </Description>
                    )}
                  </div>
                )}
                {children && <div className="px-6 py-5 text-neutral-800 dark:text-neutral-200">{children}</div>}
                {footer && (
                  <div className="flex items-center justify-end gap-2 border-t border-neutral-100 bg-neutral-50/80 dark:bg-neutral-900/60/80 px-6 py-4 dark:border-neutral-800 dark:bg-neutral-900/70">
                    {footer}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HDialog>
    </Transition>
  );
}

export { Button as DialogActionButton };
export default Dialog;
